import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { DashboardLayout } from '@/components/dashboard-layout'
import { RouteErrorBoundary } from '@/components/route-error-boundary'
import { Spin } from 'antd'
import { useCurrentUser } from '@/hooks/use-auth'

// 加载中组件
function PageLoading() {
  return (
    <div className="flex h-96 items-center justify-center">
      <Spin size="large" tip="加载中..." />
    </div>
  )
}

/**
 * 动态导入失败兜底：发版后旧 HTML 的用户访问已不存在的 chunk，自动刷新拿新 HTML + 新 chunk
 * 只重试一次，避免死循环
 */
const CHUNK_RELOAD_KEY = '__chunk_reload_retried__'
function importWithRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn()
    .then((mod) => {
      // 成功加载后清除重试标记：让后续（频繁切菜单时）不同 chunk 的偶发失败仍能各自自愈一次，
      // 而不是整个会话只在首次失败时刷新过一次后就再也不重试
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      return mod
    })
    .catch((err: Error) => {
      const isChunkError =
        /Failed to fetch dynamically imported module|Loading chunk .* failed|error loading dynamically imported module|importing a module script failed/i.test(
          err.message || ''
        )
      if (isChunkError && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
        window.location.reload()
      }
      throw err
    })
}

// 懒加载包装器
function lazyLoad(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  const LazyComponent = lazy(() => importWithRetry(importFn))
  return (
    <Suspense fallback={<PageLoading />}>
      <LazyComponent />
    </Suspense>
  )
}

// 懒加载页面
const PortalPage = lazy(() => importWithRetry(() => import('@/pages/portal/index')))
const LoginPage = lazy(() => importWithRetry(() => import('@/pages/login')))
const RegisterPage = lazy(() => importWithRetry(() => import('@/pages/register')))
const ResetPasswordPage = lazy(() => importWithRetry(() => import('@/pages/reset-password')))

// Dashboard Layout 包装器
function DashboardLayoutWrapper() {
  const location = useLocation()
  return (
    <DashboardLayout>
      {/* 按路径 key 重挂载错误边界：某页崩溃只降级内容区，切换菜单即自动恢复 */}
      <RouteErrorBoundary key={location.pathname}>
        <Outlet />
      </RouteErrorBoundary>
    </DashboardLayout>
  )
}

// 认证保护路由
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('access_token')
  if (!token) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function RoleProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode
  allowedRoles: string[]
}) {
  const { data: user, isLoading } = useCurrentUser()
  if (isLoading) return <PageLoading />
  const roles = (user?.roles as string[] | undefined) || []
  if (!roles.some((role) => allowedRoles.includes(role))) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

// 公开路由（已登录则跳转）
function PublicRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('access_token')
  if (token) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

export const router = createBrowserRouter([
  // 公开路由
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoading />}>
          <LoginPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoading />}>
          <RegisterPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoading />}>
          <ResetPasswordPage />
        </Suspense>
      </PublicRoute>
    ),
  },

  // 受保护路由 - Dashboard
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardLayoutWrapper />
      </ProtectedRoute>
    ),
    children: [
      // 首页
      {
        index: true,
        element: lazyLoad(() => import('@/pages/dashboard/index')),
      },
      // 个人设置
      {
        path: 'profile',
        element: lazyLoad(() => import('@/pages/dashboard/profile/index')),
      },
      // 通知
      {
        path: 'notifications',
        element: lazyLoad(() => import('@/pages/dashboard/notifications/index')),
      },

      // === 业务流·任务步骤页（每步一个聚焦处理页，只列待办+单一动作）===
      {
        path: 'tasks/assign',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/assign/index')),
      },
      {
        path: 'tasks/issue-invoice',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/issue-invoice/index')),
      },
      {
        path: 'tasks/voucher-audit',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/voucher-audit/index')),
      },
      {
        path: 'tasks/dispatch',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/dispatch/index')),
      },
      {
        path: 'tasks/inspect-onsite',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/inspect-onsite/index')),
      },
      {
        path: 'tasks/inspect-mail',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/inspect-mail/index')),
      },
      {
        path: 'tasks/refund',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/refund/index')),
      },
      {
        path: 'tasks/exception',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/exception/index')),
      },
      {
        path: 'tasks/cert',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/cert/index')),
      },
      {
        path: 'tasks/outsource-testing',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/outsource-testing/index')),
      },
      {
        path: 'tasks/mail-receive',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/mail-receive/index')),
      },
      {
        path: 'tasks/mail-dispatch',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/mail-dispatch/index')),
      },
      {
        path: 'tasks/mail-return',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/mail-return/index')),
      },
      {
        path: 'tasks/monthly',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/monthly/index')),
      },
      {
        path: 'tasks/invoice-approval',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/invoice-approval/index')),
      },
      {
        path: 'tasks/refund-approval',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/refund-approval/index')),
      },
      {
        path: 'tasks/reissue-approval',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/reissue-approval/index')),
      },
      {
        path: 'tasks/payment-claim',
        element: <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET']}>{lazyLoad(() => import('@/pages/dashboard/tasks/payment-claim/index'))}</RoleProtectedRoute>,
      },
      {
        path: 'tasks/payment-reconcile',
        element: <RoleProtectedRoute allowedRoles={['ADMIN', 'FINANCE']}>{lazyLoad(() => import('@/pages/dashboard/tasks/payment-reconcile/index'))}</RoleProtectedRoute>,
      },
      {
        path: 'tasks/records',
        element: lazyLoad(() => import('@/pages/dashboard/tasks/records/index')),
      },

      { path: 'market/workflow-records', element: <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET']}>{lazyLoad(() => import('@/pages/dashboard/market/workflow-records/index'))}</RoleProtectedRoute> },
      { path: 'market/payment-assistance', element: <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET']}>{lazyLoad(() => import('@/pages/dashboard/market/payment-assistance/index'))}</RoleProtectedRoute> },
      { path: 'tasks/voucher-assist', element: <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET']}>{lazyLoad(() => import('@/pages/dashboard/tasks/voucher-assist/index'))}</RoleProtectedRoute> },
      { path: 'tasks/receipt-register', element: <RoleProtectedRoute allowedRoles={['ADMIN', 'FINANCE']}>{lazyLoad(() => import('@/pages/dashboard/tasks/receipt-register/index'))}</RoleProtectedRoute> },
      { path: 'engineer/records', element: <RoleProtectedRoute allowedRoles={['ADMIN', 'ENGINEER', 'LAB_ENGINEER']}>{lazyLoad(() => import('@/pages/dashboard/engineer/records/index'))}</RoleProtectedRoute> },
      { path: 'tasks/customer', element: <RoleProtectedRoute allowedRoles={['CUSTOMER']}>{lazyLoad(() => import('@/pages/dashboard/tasks/customer/index'))}</RoleProtectedRoute> },
      // === 订单管理 ===
      {
        path: 'orders',
        element: lazyLoad(() => import('@/pages/dashboard/orders/index')),
      },
      {
        path: 'orders/batch-import',
        element: lazyLoad(() => import('@/pages/dashboard/orders/batch-import/index')),
      },
      {
        path: 'orders/public-pool',
        element: lazyLoad(() => import('@/pages/dashboard/orders/public-pool/index')),
      },
      {
        path: 'orders/assisted/onsite/new',
        element: (
          <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET', 'SERVICE']}>
            {lazyLoad(() => import('@/pages/dashboard/orders/assisted-onsite-new/index'))}
          </RoleProtectedRoute>
        ),
      },
      {
        path: 'orders/assisted/outsource/new',
        element: <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET', 'SERVICE']}>{lazyLoad(() => import('@/pages/dashboard/orders/assisted-outsource-new/index'))}</RoleProtectedRoute>,
      },
      {
        path: 'orders/assisted/mail/new',
        element: (
          <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET', 'SERVICE']}>
            {lazyLoad(() => import('@/pages/dashboard/orders/assisted-mail-new/index'))}
          </RoleProtectedRoute>
        ),
      },
      {
        path: 'orders/assisted/new',
        element: (
          <RoleProtectedRoute allowedRoles={['ADMIN', 'MARKET', 'SERVICE']}>
            {lazyLoad(() => import('@/pages/dashboard/orders/assisted-new/index'))}
          </RoleProtectedRoute>
        ),
      },
      {
        path: 'orders/:id',
        element: lazyLoad(() => import('@/components/order-detail-route')),
      },
      {
        path: 'orders/:id/devices/:deviceId/history',
        element: lazyLoad(() => import('@/pages/dashboard/orders/[id]/devices/[deviceId]/history/index')),
      },

      // === 市场部（聚合工作台已拆解，仅保留子页）===
      {
        path: 'market/orders/:id',
        element: lazyLoad(() => import('@/components/order-detail-route')),
      },
      {
        path: 'market/orders/:id/assign',
        element: lazyLoad(() => import('@/pages/dashboard/market/orders/[id]/assign/index')),
      },
      {
        path: 'market/exceptions',
        element: lazyLoad(() => import('@/pages/dashboard/market/exceptions/index')),
      },

      // === 客服部（聚合工作台已拆解，仅保留子页）===
      {
        path: 'service/dispatch-overview',
        element: lazyLoad(() => import('@/pages/dashboard/service/dispatch-overview/index')),
      },
      {
        path: 'service/schedule',
        element: lazyLoad(() => import('@/pages/dashboard/service/schedule/index')),
      },
      {
        path: 'return-ledger',
        element: lazyLoad(() => import('@/pages/dashboard/return-ledger/index')),
      },
      {
        path: 'service/outsource-workbench',
        element: lazyLoad(() => import('@/pages/dashboard/service/outsource-workbench/index')),
      },
      {
        path: 'service/mail-workbench',
        element: lazyLoad(() => import('@/pages/dashboard/service/mail-workbench/index')),
      },
      {
        path: 'service/orders/:id',
        element: lazyLoad(() => import('@/components/order-detail-route')),
      },
      {
        path: 'service/orders/:id/dispatch',
        element: lazyLoad(() => import('@/pages/dashboard/service/orders/[id]/dispatch/index')),
      },
      {
        path: 'service/mail-tracking',
        element: lazyLoad(() => import('@/pages/dashboard/service/mail-tracking/index')),
      },
      {
        path: 'service/certificates',
        element: lazyLoad(() => import('@/pages/dashboard/service/certificates/index')),
      },
      {
        path: 'service/inspection-records',
        element: lazyLoad(() => import('@/pages/dashboard/service/inspection-records/index')),
      },
      // === 工程师（聚合工作台已拆解，检测队列见 tasks/inspect-onsite|mail）===
      {
        path: 'onsite-orders',
        element: lazyLoad(() => import('@/pages/dashboard/onsite-orders/index')),
      },
      {
        path: 'engineer/tasks/:id',
        element: lazyLoad(() => import('@/pages/dashboard/engineer/tasks/[id]/index')),
      },

      // === 财务部（聚合工作台已拆解，仅保留子页）===
      {
        path: 'finance/orders',
        element: <RoleProtectedRoute allowedRoles={['ADMIN', 'FINANCE']}>{lazyLoad(() => import('@/pages/dashboard/finance/orders/index'))}</RoleProtectedRoute>,
      },
      {
        path: 'finance/orders/:id',
        element: lazyLoad(() => import('@/components/order-detail-route')),
      },
      {
        path: 'finance/payments',
        element: lazyLoad(() => import('@/pages/dashboard/finance/payments/index')),
      },
      {
        path: 'finance/invoices',
        element: lazyLoad(() => import('@/pages/dashboard/finance/invoices/index')),
      },
      {
        path: 'finance/cost-profit',
        element: lazyLoad(() => import('@/pages/dashboard/finance/cost-profit/index')),
      },
      {
        path: 'finance/prepayments',
        element: lazyLoad(() => import('@/pages/dashboard/finance/prepayments/index')),
      },
      {
        path: 'finance/refunds',
        element: lazyLoad(() => import('@/pages/dashboard/finance/refunds/index')),
      },
      {
        path: 'finance/reissue-pending',
        element: lazyLoad(() => import('@/pages/dashboard/finance/reissue-pending/index')),
      },
      {
        path: 'finance/monthly',
        element: lazyLoad(() => import('@/pages/dashboard/finance/monthly/index')),
      },
      {
        path: 'finance/monthly/reconciliation',
        element: lazyLoad(
          () => import('@/pages/dashboard/finance/monthly/reconciliation/index')
        ),
      },

      // === 月结公司 ===
      {
        path: 'monthly-company',
        element: lazyLoad(() => import('@/pages/dashboard/monthly-company/index')),
      },
      {
        path: 'monthly-company/info',
        element: lazyLoad(() => import('@/pages/dashboard/monthly-company/info/index')),
      },
      {
        path: 'monthly-company/orders',
        element: lazyLoad(() => import('@/pages/dashboard/monthly-company/orders/index')),
      },

      // === 客户管理 ===
      {
        path: 'customer',
        element: lazyLoad(() => import('@/pages/dashboard/customer/index')),
      },
      {
        path: 'customer/users',
        element: lazyLoad(() => import('@/pages/dashboard/customer/users/index')),
      },
      {
        path: 'customer/users/:id/orders',
        element: lazyLoad(() => import('@/pages/dashboard/customer/users/[id]/orders/index')),
      },
      {
        path: 'customer/companies',
        element: lazyLoad(() => import('@/pages/dashboard/customer/companies/index')),
      },
      {
        path: 'customer/companies/:id/customers',
        element: lazyLoad(() => import('@/pages/dashboard/customer/companies/[id]/customers/index')),
      },
      {
        path: 'customer/centers',
        element: lazyLoad(() => import('@/pages/dashboard/customer/centers/index')),
      },
      {
        path: 'customer/management',
        element: lazyLoad(() => import('@/pages/dashboard/customer/management/index')),
      },

      // === 我的订单（客户视角）===
      {
        path: 'my-orders',
        element: lazyLoad(() => import('@/pages/dashboard/my-orders/index')),
      },
      {
        path: 'my-orders/new',
        element: (
          <RoleProtectedRoute allowedRoles={['CUSTOMER']}>
            {lazyLoad(() => import('@/pages/dashboard/my-orders/new/index'))}
          </RoleProtectedRoute>
        ),
      },
      {
        path: 'my-orders/:id',
        element: lazyLoad(() => import('@/components/order-detail-route')),
      },
      {
        path: 'my-payments',
        element: lazyLoad(() => import('@/pages/dashboard/my-payments/index')),
      },
      {
        path: 'my-invoices',
        element: lazyLoad(() => import('@/pages/dashboard/my-invoices/index')),
      },
      {
        path: 'my-invoices/:id',
        element: lazyLoad(() => import('@/pages/dashboard/my-invoices/[id]/index')),
      },
      {
        path: 'my-certificates',
        element: lazyLoad(() => import('@/pages/dashboard/my-certificates/index')),
      },
      {
        path: 'my-company',
        element: lazyLoad(() => import('@/pages/dashboard/my-company/index')),
      },
      {
        path: 'my-refunds',
        element: lazyLoad(() => import('@/pages/dashboard/my-refunds/index')),
      },
      {
        path: 'my-refunds/:id',
        element: lazyLoad(() => import('@/pages/dashboard/my-refunds/[id]/index')),
      },
      {
        path: 'my-monthly',
        element: lazyLoad(() => import('@/pages/dashboard/my-monthly/index')),
      },
      {
        path: 'my-profile',
        element: lazyLoad(() => import('@/pages/dashboard/my-profile/index')),
      },
      {
        path: 'my-check/device/:id',
        element: lazyLoad(() => import('@/pages/dashboard/my-check/device/[id]/index')),
      },
      {
        path: 'my-check/order/:id',
        element: lazyLoad(() => import('@/components/order-detail-route')),
      },

      // === 配置管理 ===
      // 注：config 概览页 + 服务项目/零配件已随价目 v2 废弃删除（2026-07-02）
      {
        path: 'config/staff',
        element: lazyLoad(() => import('@/pages/dashboard/config/staff/index')),
      },
      {
        path: 'config/device-types',
        element: lazyLoad(() => import('@/pages/dashboard/config/device-types/index')),
      },
      {
        path: 'config/suppliers',
        element: lazyLoad(() => import('@/pages/dashboard/config/suppliers/index')),
      },
      {
        path: 'config/inspection-templates',
        element: lazyLoad(() => import('@/pages/dashboard/config/inspection-templates/index')),
      },
      {
        path: 'config/company-discounts',
        element: lazyLoad(() => import('@/pages/dashboard/config/company-discounts/index')),
      },
      {
        path: 'config/additional-fees',
        element: lazyLoad(() => import('@/pages/dashboard/config/additional-fees/index')),
      },

      // === 基础数据 ===
      {
        path: 'base-data',
        element: lazyLoad(() => import('@/pages/dashboard/base-data/index')),
      },

      // === 审计日志 ===
      {
        path: 'audit-logs',
        element: lazyLoad(() => import('@/pages/dashboard/audit-logs/index')),
      },

      // === 化身审计 ===
      {
        path: 'impersonation/history',
        element: lazyLoad(() => import('@/pages/dashboard/impersonation/history/index')),
      },
    ],
  },

  // 门户首页
  {
    path: '/',
    element: (
      <Suspense fallback={<PageLoading />}>
        <PortalPage />
      </Suspense>
    ),
  },

  // 404 处理
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
])
